import React, { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Tabs,
  Tag,
  Timeline,
  Typography,
  message,
} from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import PmvEquipmentComponentsPanel from '@/components/pmv/PmvEquipmentComponentsPanel';
import { openPmvPassportPrint } from '@/components/pmv/PrintablePmvCommercial';
import WorkshopPassportStrip from '@/pages/assets/workshop/WorkshopPassportStrip';
import pmvService, {
  type PmvInspectionResult,
  type PmvInspectionTemplate,
  type PmvOperatorAssignment,
  type PmvReadinessResult,
} from '@/services/pmv.service';
import pmvHireService, { formatHireMoney, type PmvHireRate } from '@/services/pmv-hire.service';
import employeeService from '@/services/employee.service';
import { useProjects } from '@/hooks/useProjects';
import { buildEmployeeIdToLabelMap, resolveEmployeeDisplayFromMap } from '@/utils/userDisplay';
import {
  buildProjectIdToLabelMap,
  formatEmployeeOption,
  resolveProjectDisplayFromMap,
} from '@/utils/pmvDisplay';
import {
  humanizeFuelExceptionReason,
  humanizeReadinessOverall,
  humanizeReadinessSectionKey,
  passportPrimaryAction,
} from '@/utils/pmvUxCopy';
import { PmvPassportConnectedPanel } from '@/pages/assets/pmv/PmvConnectedPages';
import { connectivityLabel, connectivityTagColor, formatLastSeen } from '@/utils/pmvConnectedCopy';

const { Text, Title } = Typography;

const Pmv360UsagePanel: React.FC<{ profileId: string; navigate: (path: string) => void }> = ({
  profileId,
  navigate,
}) => {
  const today = new Date().toISOString().slice(0, 10);
  const d7 = new Date();
  d7.setUTCDate(d7.getUTCDate() - 7);
  const d30 = new Date();
  d30.setUTCDate(d30.getUTCDate() - 30);

  const logsQ = useQuery({
    queryKey: ['pmv-profile-logs', profileId],
    queryFn: () => pmvService.listDailyLogs({ profile_id: profileId, pageSize: 10 }),
    enabled: Boolean(profileId),
  });
  const metersQ = useQuery({
    queryKey: ['pmv-profile-meters', profileId],
    queryFn: () => pmvService.listProfileMeters(profileId),
    enabled: Boolean(profileId),
  });
  const util7Q = useQuery({
    queryKey: ['pmv-profile-util-7d', profileId],
    queryFn: () => pmvService.getProfileUtilization(profileId, d7.toISOString().slice(0, 10), today),
    enabled: Boolean(profileId),
  });
  const util30Q = useQuery({
    queryKey: ['pmv-profile-util-30d', profileId],
    queryFn: () => pmvService.getProfileUtilization(profileId, d30.toISOString().slice(0, 10), today),
    enabled: Boolean(profileId),
  });

  const todayLog = (logsQ.data?.data ?? []).find((l) => l.operating_date === today);
  const util7 = util7Q.data?.aggregate?.workingUtilization;
  const util30 = util30Q.data?.aggregate?.workingUtilization;

  if (!logsQ.data?.data?.length && !metersQ.data?.length) {
    return <Empty description="No operational logs yet — create from active allocation workspace." />;
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card size="small" title="Today">
            {todayLog ? (
              <Button type="link" onClick={() => navigate(`/asset/pmv/daily-logs/${todayLog.id}`)}>
                {todayLog.log_number} — {todayLog.status}
              </Button>
            ) : (
              <Text type="secondary">No log for today</Text>
            )}
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small" title="7-day utilization">
            <Text>{util7 != null ? `${util7}%` : 'Utilization unavailable — certified daily logs required'}</Text>
          </Card>
        </Col>
        <Col xs={12} sm={8}>
          <Card size="small" title="30-day utilization">
            <Text>{util30 != null ? `${util30}%` : 'Utilization unavailable — certified daily logs required'}</Text>
          </Card>
        </Col>
      </Row>
      {metersQ.data?.[0] && (
        <Card size="small" title="Latest meter">
          <Text>{Number(metersQ.data[0].reading_value).toFixed(1)} {metersQ.data[0].meter_unit}</Text>
        </Card>
      )}
      <Card size="small" title="Recent daily logs">
        <Timeline
          items={(logsQ.data?.data ?? []).map((l) => ({
            children: (
              <Button type="link" onClick={() => navigate(`/asset/pmv/daily-logs/${l.id}`)}>
                {l.log_number} — {l.operating_date} ({l.status})
              </Button>
            ),
          }))}
        />
      </Card>
    </Space>
  );
};

const resultColor = (result: string) => {
  if (result === 'PASS') return 'success';
  if (result === 'PASS_WITH_DEFECT') return 'warning';
  if (result === 'FAIL') return 'error';
  return 'default';
};

const Pmv360InspectionPanel: React.FC<{
  profileId: string;
  classificationId?: string;
  navigate: (path: string) => void;
}> = ({ profileId, navigate }) => {
  const qc = useQueryClient();
  const templatesQ = useQuery({
    queryKey: ['pmv-inspection-templates', profileId],
    queryFn: () => pmvService.listProfileInspectionTemplates(profileId),
    enabled: Boolean(profileId),
  });
  const inspectionsQ = useQuery({
    queryKey: ['pmv-inspections', profileId],
    queryFn: () => pmvService.listProfileInspections(profileId),
    enabled: Boolean(profileId),
  });

  const voidMut = useMutation({
    mutationFn: ({ resultId, reason }: { resultId: string; reason: string }) =>
      pmvService.voidInspectionResult(resultId, reason),
    onSuccess: () => {
      message.success('Inspection voided');
      void qc.invalidateQueries({ queryKey: ['pmv-inspections', profileId] });
      void qc.invalidateQueries({ queryKey: ['pmv-workspace', profileId] });
    },
    onError: (e: Error) => message.error(e.message || 'Void failed'),
  });

  const confirmVoid = (row: PmvInspectionResult) => {
    let reason = '';
    Modal.confirm({
      title: 'Void inspection?',
      content: (
        <Input.TextArea
          rows={3}
          placeholder="Reason for void"
          onChange={(e) => {
            reason = e.target.value;
          }}
        />
      ),
      okText: 'Void',
      okButtonProps: { danger: true },
      onOk: () => {
        const trimmed = reason.trim();
        if (!trimmed) {
          message.error('Reason is required');
          return Promise.reject(new Error('Reason required'));
        }
        return voidMut.mutateAsync({ resultId: row.id, reason: trimmed });
      },
    });
  };

  const openTemplate = (tpl: PmvInspectionTemplate) => {
    navigate(`/asset/pmv/${profileId}/inspections/new?template_id=${tpl.id}`);
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Card size="small" title="Inspection templates">
        {(templatesQ.data ?? []).length === 0 ? (
          <Empty description="No inspection templates configured for this classification." />
        ) : (
          <Space wrap>
            {(templatesQ.data ?? []).map((tpl) => (
              <Button key={tpl.id} onClick={() => openTemplate(tpl)}>
                {tpl.template_name} ({tpl.inspection_kind.replace(/_/g, ' ')})
              </Button>
            ))}
          </Space>
        )}
      </Card>
      <Card size="small" title="Inspection history">
        {(inspectionsQ.data ?? []).length === 0 ? (
          <Empty description="No inspections recorded yet." />
        ) : (
          <Timeline
            items={(inspectionsQ.data ?? []).map((r) => ({
              children: (
                <Space wrap>
                  <Text strong>{r.inspection_number}</Text>
                  <Tag color={resultColor(r.overall_result)}>{r.overall_result}</Tag>
                  <Tag>{r.status}</Tag>
                  <Text type="secondary">
                    {new Date(r.inspected_at).toLocaleString()}
                    {r.valid_until ? ` — valid until ${String(r.valid_until).slice(0, 10)}` : ''}
                  </Text>
                  {r.status === 'SUBMITTED' && (
                    <Button size="small" danger loading={voidMut.isPending} onClick={() => confirmVoid(r)}>
                      Void
                    </Button>
                  )}
                </Space>
              ),
            }))}
          />
        )}
      </Card>
      {profileId && (
        <EntityAttachmentsCard entityType="PMV_PROFILE" entityId={profileId} title="Inspection evidence" />
      )}
    </Space>
  );
};

const breakdownStatusColor = (status: string) => {
  if (status === 'OPEN') return 'error';
  if (status === 'ESCALATED') return 'warning';
  if (status === 'RESOLVED') return 'success';
  return 'default';
};

const Pmv360MaintenancePanel: React.FC<{
  profileId: string;
  navigate: (path: string) => void;
  maintenance?: {
    plans?: Array<{ id: string; title: string; next_due_date?: string | null }>;
    cases?: Array<{ id: string; case_number: string; title: string; status: string }>;
  };
}> = ({ profileId, navigate, maintenance }) => {
  const qc = useQueryClient();

  const breakdownsQ = useQuery({
    queryKey: ['pmv-breakdowns', profileId],
    queryFn: () => pmvService.listProfileBreakdowns(profileId),
    enabled: Boolean(profileId),
  });

  const resolveMut = useMutation({
    mutationFn: (eventId: string) => pmvService.resolveBreakdownEvent(eventId),
    onSuccess: () => {
      message.success('Breakdown resolved');
      void qc.invalidateQueries({ queryKey: ['pmv-breakdowns', profileId] });
      void qc.invalidateQueries({ queryKey: ['pmv-workspace', profileId] });
    },
    onError: (e: Error) => message.error(e.message || 'Resolve failed'),
  });

  const escalateMut = useMutation({
    mutationFn: (eventId: string) => pmvService.escalateBreakdownEvent(eventId),
    onSuccess: () => {
      message.success('Escalated to maintenance case');
      void qc.invalidateQueries({ queryKey: ['pmv-breakdowns', profileId] });
      void qc.invalidateQueries({ queryKey: ['pmv-workspace', profileId] });
    },
    onError: (e: Error) => message.error(e.message || 'Escalation failed'),
  });

  const openBreakdown = (breakdownsQ.data ?? []).find((b) => b.status === 'OPEN' || b.status === 'ESCALATED');

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Card
        size="small"
        title="Breakdown events"
        extra={
          !openBreakdown ? (
            <Button type="primary" danger onClick={() => navigate(`/asset/pmv/${profileId}/breakdowns/new`)}>
              Report breakdown
            </Button>
          ) : null
        }
      >
        {(breakdownsQ.data ?? []).length === 0 ? (
          <Empty description="No breakdown events — unit has no open field failures recorded." />
        ) : (
          <Timeline
            items={(breakdownsQ.data ?? []).map((b) => ({
              children: (
                <Space direction="vertical" size={4}>
                  <Space wrap>
                    <Text strong>{b.event_number}</Text>
                    <Tag color={breakdownStatusColor(b.status)}>{b.status}</Tag>
                    <Tag>{b.severity}</Tag>
                    <Text>{b.title}</Text>
                  </Space>
                  <Text type="secondary">{new Date(b.occurred_at).toLocaleString()}</Text>
                  {b.maintenance_case_id && (
                    <Text type="secondary">Maintenance case linked</Text>
                  )}
                  {(b.status === 'OPEN' || b.status === 'ESCALATED') && (
                    <Space>
                      {b.status === 'OPEN' && (
                        <Button size="small" onClick={() => escalateMut.mutate(b.id)} loading={escalateMut.isPending}>
                          Escalate to maintenance
                        </Button>
                      )}
                      <Button size="small" onClick={() => resolveMut.mutate(b.id)} loading={resolveMut.isPending}>
                        Resolve
                      </Button>
                    </Space>
                  )}
                </Space>
              ),
            }))}
          />
        )}
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card size="small" title="PM plans">
            {(maintenance?.plans ?? []).length === 0 ? (
              <Empty description="No preventive maintenance plans linked to this unit." />
            ) : (
              <ul style={{ paddingLeft: 18, margin: 0 }}>
                {(maintenance?.plans ?? []).map((p) => (
                  <li key={p.id}>
                    {p.title}
                    {p.next_due_date ? ` — due ${String(p.next_due_date).slice(0, 10)}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card size="small" title="Maintenance cases">
            {(maintenance?.cases ?? []).length === 0 ? (
              <Empty description="No open or recent maintenance cases for this unit." />
            ) : (
              <ul style={{ paddingLeft: 18, margin: 0 }}>
                {(maintenance?.cases ?? []).map((c) => (
                  <li key={c.id}>
                    {c.case_number} — {c.title} <Tag>{c.status}</Tag>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </Col>
      </Row>
    </Space>
  );
};

const healthTagColor = (s: string) => {
  if (s === 'CRITICAL' || s === 'POOR') return 'red';
  if (s === 'DEGRADED' || s === 'WATCH') return 'orange';
  if (s === 'HEALTHY') return 'green';
  return 'default';
};

const Pmv360ReliabilityPanel: React.FC<{
  profileId: string;
  navigate: (path: string) => void;
}> = ({ profileId, navigate }) => {
  const healthQ = useQuery({
    queryKey: ['pmv-reliability-health', profileId],
    queryFn: () => pmvService.getEquipmentReliabilityHealth(profileId),
    enabled: Boolean(profileId),
  });
  const h = healthQ.data;
  const state = h?.health_state || 'UNKNOWN';

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="Health & reliability — OBSERVE / RECOMMEND"
        description="Does not change PMV status or auto-create work orders. UNKNOWN means insufficient evidence — never treated as HEALTHY 100."
        action={
          <Space direction="vertical">
            <Button
              size="small"
              onClick={() => navigate('/asset/pmv/reliability/control-tower')}
            >
              Reliability Tower
            </Button>
            <Button size="small" onClick={() => navigate('/asset/pmv/reliability')}>
              Health Register
            </Button>
          </Space>
        }
      />
      <Card size="small" title="Equipment health" loading={healthQ.isLoading}>
        <Descriptions column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label="Health">
            <Tag color={healthTagColor(state)}>{state}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Confidence">
            {h?.health_confidence || 'INSUFFICIENT'}
          </Descriptions.Item>
          <Descriptions.Item label="Score">
            {h?.health_score == null ? 'Unavailable' : Number(h.health_score).toFixed(0)}
          </Descriptions.Item>
          <Descriptions.Item label="Primary driver">
            {h?.primary_driver || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="MTBF (h)">
            {h?.mtbf_hours == null ? 'Unavailable' : Number(h.mtbf_hours).toFixed(1)}
          </Descriptions.Item>
          <Descriptions.Item label="Availability">
            {h?.availability_pct == null
              ? 'Unavailable'
              : `${Number(h.availability_pct).toFixed(1)}%`}
          </Descriptions.Item>
        </Descriptions>
        {h?.note ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {h.note}
          </Typography.Text>
        ) : null}
      </Card>
    </Space>
  );
};

const Pmv360FuelPanel: React.FC<{
  profileId: string;
  navigate: (path: string) => void;
}> = ({ profileId, navigate }) => {
  const d30 = new Date();
  d30.setUTCDate(d30.getUTCDate() - 30);
  const today = new Date().toISOString().slice(0, 10);
  const from30 = d30.toISOString().slice(0, 10);

  const fuelQ = useQuery({
    queryKey: ['pmv-fuel', profileId],
    queryFn: () => pmvService.listProfileFuelAttributions(profileId),
    enabled: Boolean(profileId),
  });
  const summaryQ = useQuery({
    queryKey: ['pmv-fuel-summary', profileId, from30, today],
    queryFn: () => pmvService.getProfileFuelSummary(profileId, from30, today),
    enabled: Boolean(profileId),
  });

  const summary = summaryQ.data?.summary;

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="Fuel intelligence — authoritative vs observed"
        description="Inventory remains stock authority. Telemetry fuel level is evidence only. Missing efficiency is shown as Unavailable, never as 0."
        action={
          <Space direction="vertical">
            <Button size="small" onClick={() => navigate('/asset/pmv/fuel/control-tower')}>
              Fuel Control Tower
            </Button>
            <Button size="small" onClick={() => navigate('/asset/pmv/fuel/reconciliation')}>
              Reconciliation
            </Button>
          </Space>
        }
      />
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card size="small" title="30-day total">
            <Text strong>
              {summary?.totalLiters != null ? `${summary.totalLiters.toFixed(1)} L` : 'Unavailable'}
            </Text>
            {summary?.totalCost != null && (
              <div>
                <Text type="secondary">Operational cost ref: {summary.totalCost.toFixed(2)} (≠ GL)</Text>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={16}>
          <Card size="small" title="Efficiency KPI">
            {summary?.primaryKpi ? (
              <Text>
                {summary.primaryKpi.label}: {summary.primaryKpi.value} {summary.primaryKpi.unit}
              </Text>
            ) : (
              <Text type="secondary">
                {summary?.denominatorNote ??
                  'Consumption cannot be calculated because valid operating hours or distance are missing.'}
              </Text>
            )}
          </Card>
        </Col>
      </Row>

      <Card
        size="small"
        title="Fuel attributions"
        extra={
          <Button type="primary" onClick={() => navigate(`/asset/pmv/${profileId}/fuel/new`)}>
            Record fill
          </Button>
        }
      >
        {(fuelQ.data ?? []).length === 0 ? (
          <Empty description="No fuel transactions are linked to this equipment." />
        ) : (
          <Timeline
            items={(fuelQ.data ?? []).map((a) => {
              const missingProject = !a.project_id;
              const auth =
                (a as { quantity_authority?: string }).quantity_authority ||
                (a.material_issuance_id
                  ? 'Quantity linked to Inventory issue — stock authority remains Inventory.'
                  : 'Operational fuel attribution only — does not change Inventory stock.');
              return {
                children: (
                  <Space direction="vertical" size={2}>
                    <Space wrap>
                      <Text strong>{a.attribution_number}</Text>
                      <Tag>{a.fuel_type}</Tag>
                      <Text>{a.quantity_liters} L</Text>
                      {a.total_cost != null && <Text type="secondary">({a.total_cost})</Text>}
                      {a.source_type && <Tag color="blue">{a.source_type}</Tag>}
                      {missingProject && (
                        <>
                          <Tag color="orange">{humanizeFuelExceptionReason('MISSING_PROJECT')}</Tag>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            MISSING_PROJECT
                          </Text>
                        </>
                      )}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {auth}
                    </Text>
                    <Text type="secondary">{new Date(a.attributed_at).toLocaleString()}</Text>
                  </Space>
                ),
              };
            })}
          />
        )}
      </Card>
    </Space>
  );
};

function ReadinessPanel({ readiness }: { readiness: PmvReadinessResult }) {
  const overall = humanizeReadinessOverall(readiness.overall);
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space align="center" wrap>
        <Text strong>Deployment readiness</Text>
        <Tag color={overall.color} style={{ fontSize: 14, padding: '2px 10px' }}>
          {overall.label}
        </Tag>
      </Space>
      {readiness.blockers.map((b) => (
        <Alert key={b} type="error" message={b} showIcon />
      ))}
      {readiness.warnings?.map((w) => (
        <Alert key={w} type="warning" message={w} showIcon />
      ))}
      {readiness.sections.map((sec) => (
        <Card key={sec.key} size="small" title={humanizeReadinessSectionKey(sec.key)}>
          <Tag
            color={
              sec.status === 'PASS'
                ? 'success'
                : sec.status === 'WARNING'
                  ? 'warning'
                  : sec.status === 'BLOCKED'
                    ? 'error'
                    : 'default'
            }
          >
            {sec.status === 'PASS' ? 'READY' : sec.status}
          </Tag>
          <ul style={{ marginTop: 8, paddingLeft: 18 }}>
            {sec.checks.map((c) => (
              <li key={c.key}>
                {c.label}: <Tag>{c.status === 'PASS' ? 'READY' : c.status}</Tag>
                {c.message ? ` — ${c.message}` : ''}
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </Space>
  );
}

const Pmv360OperatorsPanel: React.FC<{
  profileId: string;
  navigate: (path: string) => void;
  operators?: { assignments?: PmvOperatorAssignment[]; current?: PmvOperatorAssignment | null };
}> = ({ profileId, navigate, operators }) => {
  const qc = useQueryClient();

  const assignQ = useQuery({
    queryKey: ['pmv-operator-assignments', profileId],
    queryFn: () => pmvService.listProfileOperatorAssignments(profileId),
    enabled: Boolean(profileId),
  });

  const employeesQ = useQuery({
    queryKey: ['pmv-operator-employees'],
    queryFn: () => employeeService.list({ employment_status: 'Active' }),
  });

  const endMut = useMutation({
    mutationFn: (assignmentId: string) => pmvService.endOperatorAssignment(assignmentId),
    onSuccess: () => {
      message.success('Assignment ended');
      void qc.invalidateQueries({ queryKey: ['pmv-operator-assignments', profileId] });
      void qc.invalidateQueries({ queryKey: ['pmv-workspace', profileId] });
    },
    onError: (e: Error) => message.error(e.message || 'End failed'),
  });

  const current = operators?.current ?? assignQ.data?.find((a) => a.status === 'ACTIVE') ?? null;
  const history = assignQ.data ?? operators?.assignments ?? [];
  const employeeLabels = buildEmployeeIdToLabelMap(
    (employeesQ.data ?? []).map((e) => ({
      id: e.id,
      first_name: e.first_name,
      last_name: e.last_name,
      employee_code: e.employee_code,
    }))
  );
  const employeeDisplay = (employeeId: string) => {
    const fromMap = resolveEmployeeDisplayFromMap(employeeLabels, employeeId);
    if (fromMap !== 'Unknown employee') return fromMap;
    const e = (employeesQ.data ?? []).find((x) => x.id === employeeId);
    return e ? formatEmployeeOption(e) : fromMap;
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Card
        size="small"
        title="Current operator"
        extra={
          !current ? (
            <Button type="primary" onClick={() => navigate(`/asset/pmv/${profileId}/operators/assign`)}>
              Assign operator
            </Button>
          ) : (
            <Button danger onClick={() => endMut.mutate(current.id)} loading={endMut.isPending}>
              End assignment
            </Button>
          )
        }
      >
        {current ? (
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Assignment">{current.assignment_number}</Descriptions.Item>
            <Descriptions.Item label="Employee">{employeeDisplay(current.employee_id)}</Descriptions.Item>
            <Descriptions.Item label="Role">{current.assignment_role}</Descriptions.Item>
            <Descriptions.Item label="From">{current.assigned_from}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Alert
            type="warning"
            showIcon
            message="No active operator assigned"
            description="Deployment readiness may be blocked when operator competency is required for this class."
          />
        )}
      </Card>

      <Card size="small" title="Assignment history">
        {history.length === 0 ? (
          <Empty description="No assignments yet" />
        ) : (
          <Timeline
            items={history.map((a) => ({
              color: a.status === 'ACTIVE' ? 'green' : 'gray',
              children: `${a.assignment_number} — ${employeeDisplay(a.employee_id)} — ${a.assignment_role} (${a.status})`,
            }))}
          />
        )}
      </Card>
    </Space>
  );
};

const Pmv360HirePanel: React.FC<{
  profileId: string;
  ownershipType?: string | null;
  navigate: (path: string) => void;
}> = ({ profileId, ownershipType, navigate }) => {
  const agreementsQ = useQuery({
    queryKey: ['pmv-hire-agreements-profile', profileId],
    queryFn: () => pmvHireService.listAgreements({ pmv_profile_id: profileId, limit: 20 }),
    enabled: Boolean(profileId),
  });
  const agreement = agreementsQ.data?.items?.[0];
  const ratesQ = useQuery({
    queryKey: ['pmv-hire-rate-effective-passport', agreement?.id],
    queryFn: () => pmvHireService.resolveEffectiveRate(agreement!.id),
    enabled: Boolean(agreement?.id),
  });
  const timesheetsQ = useQuery({
    queryKey: ['pmv-hire-ts-profile', agreement?.id],
    queryFn: () =>
      pmvHireService.listTimesheets({ hire_agreement_id: agreement!.id, limit: 5 }),
    enabled: Boolean(agreement?.id),
  });
  const reconQ = useQuery({
    queryKey: ['pmv-hire-recon-profile', agreement?.id],
    queryFn: () =>
      pmvHireService.listReconciliations({ hire_agreement_id: agreement!.id, limit: 5 }),
    enabled: Boolean(agreement?.id),
  });

  const effective = ratesQ.data;
  const effectiveRate =
    effective && typeof effective === 'object' && 'rate' in effective
      ? (effective as { rate: PmvHireRate | null }).rate
      : (effective as PmvHireRate | null);
  const rateAvailable =
    effective && typeof effective === 'object' && 'available' in effective
      ? !!(effective as { available?: boolean }).available
      : !!effectiveRate;
  const currentRate = (() => {
    if (!rateAvailable || !effectiveRate) return 'Unavailable';
    const raw =
      effectiveRate.rate_basis === 'HOURLY'
        ? effectiveRate.hourly_rate
        : effectiveRate.rate_basis === 'DAILY'
          ? effectiveRate.daily_rate
          : effectiveRate.monthly_rate;
    if (raw == null || raw === '') return 'Unavailable';
    return `${formatHireMoney(raw, effectiveRate.currency)} / ${effectiveRate.rate_basis}`;
  })();

  const ownershipLabel =
    ownershipType === 'RENTED' || ownershipType === 'LEASED'
      ? `Hired (${ownershipType})`
      : ownershipType || '—';

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="Hire commercial panel — preview only"
        description="Backend is commercial interpretation authority. Missing rate is Unavailable, never 0. AP/GL stay outside PMV."
      />
      {!agreement ? (
        <Empty description="No hire agreement linked to this PMV profile.">
          <Button onClick={() => navigate('/asset/pmv/hire/agreements/new')}>New agreement</Button>
        </Empty>
      ) : (
        <>
          <Card size="small" title="Commercial hire">
            <Descriptions size="small" column={{ xs: 1, sm: 2 }} bordered>
              <Descriptions.Item label="Ownership">{ownershipLabel}</Descriptions.Item>
              <Descriptions.Item label="Vendor">{agreement.supplier_id}</Descriptions.Item>
              <Descriptions.Item label="Agreement">
                <Button
                  type="link"
                  style={{ padding: 0 }}
                  onClick={() => navigate(`/asset/pmv/hire/agreements/${agreement.id}`)}
                >
                  {agreement.agreement_number}
                </Button>
              </Descriptions.Item>
              <Descriptions.Item label="PO">{agreement.purchase_order_id || '—'}</Descriptions.Item>
              <Descriptions.Item label="Hire state">
                <Tag>{agreement.commercial_status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="On-hire">
                {agreement.commercial_on_hire_at
                  ? String(agreement.commercial_on_hire_at).slice(0, 19)
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Off-hire effective">
                {agreement.commercial_off_hire_effective_at
                  ? String(agreement.commercial_off_hire_effective_at).slice(0, 19)
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Rate basis">
                {effectiveRate?.rate_basis || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Current rate">{currentRate}</Descriptions.Item>
              <Descriptions.Item label="Operator resp.">
                {agreement.operator_responsibility || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Fuel resp.">
                {agreement.fuel_responsibility || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Maint. resp.">
                {agreement.maintenance_responsibility || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Planned period">
                {agreement.planned_on_hire_date || '—'} → {agreement.planned_off_hire_date || '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card size="small" title="Timesheet / invoice status">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">
                Latest timesheet:{' '}
                {timesheetsQ.data?.items?.[0]
                  ? `${timesheetsQ.data.items[0].timesheet_number || timesheetsQ.data.items[0].id.slice(0, 8)} (${timesheetsQ.data.items[0].status})`
                  : '—'}
              </Text>
              <Text type="secondary">
                Latest recon:{' '}
                {reconQ.data?.items?.[0]
                  ? `${reconQ.data.items[0].status} / ${reconQ.data.items[0].variance_type || '—'}`
                  : '—'}
              </Text>
              <Space wrap>
                <Button size="small" onClick={() => navigate('/asset/pmv/hire/timesheets')}>
                  Timesheets
                </Button>
                <Button size="small" onClick={() => navigate('/asset/pmv/hire/reconciliations')}>
                  Invoice recon
                </Button>
                <Button size="small" onClick={() => navigate('/asset/pmv/hire/control')}>
                  Hire Control
                </Button>
              </Space>
            </Space>
          </Card>
        </>
      )}
    </Space>
  );
};

const Pmv360CostPanel: React.FC<{
  profileId: string;
  projectId?: string | null;
  costs?: { projectCosts?: Array<Record<string, unknown>>; summary?: { totalActual?: number; byKind?: Array<{ kind: string; total: number }>; currency?: string } };
}> = ({ profileId, projectId, costs }) => {
  const summaryQ = useQuery({
    queryKey: ['pmv-cost-summary', profileId],
    queryFn: () => pmvService.getProfileCostSummary(profileId),
    enabled: Boolean(profileId),
  });
  const costsQ = useQuery({
    queryKey: ['pmv-project-costs', profileId],
    queryFn: () => pmvService.listProfileProjectCosts(profileId),
    enabled: Boolean(profileId),
  });
  const budgetQ = useQuery({
    queryKey: ['pmv-budget-compare', profileId, projectId],
    queryFn: () => pmvService.getProfileBudgetCompare(profileId, projectId!),
    enabled: Boolean(profileId && projectId),
  });

  const summary = summaryQ.data ?? costs?.summary;
  const rows = costsQ.data ?? (costs?.projectCosts as typeof costsQ.data) ?? [];

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card size="small" title="PMV actuals">
            <Text strong>{summary?.totalActual?.toFixed(2) ?? '—'} {summary?.currency ?? 'AED'}</Text>
          </Card>
        </Col>
        <Col xs={24} sm={16}>
          <Card size="small" title="By kind">
            {(summary?.byKind ?? []).length === 0 ? (
              <Text type="secondary">No PMV-attributed project costs yet.</Text>
            ) : (
              summary!.byKind!.map((k) => (
                <div key={k.kind}>
                  {k.kind}: {k.total.toFixed(2)}
                </div>
              ))
            )}
          </Card>
        </Col>
      </Row>

      {projectId && (
        <Card size="small" title="Budget compare (Original)">
          {budgetQ.isLoading ? (
            <Spin />
          ) : budgetQ.data ? (
            <Descriptions column={1} size="small">
              <Descriptions.Item label="PMV actual">{budgetQ.data.pmvActualTotal.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="Original equipment budget">
                {budgetQ.data.hasOriginalBudget
                  ? budgetQ.data.originalBudgetEquipment?.toFixed(2)
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Variance">
                {budgetQ.data.variance != null ? budgetQ.data.variance.toFixed(2) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Note">{budgetQ.data.note}</Descriptions.Item>
            </Descriptions>
          ) : (
            <Text type="secondary">Budget compare unavailable.</Text>
          )}
        </Card>
      )}

      <Card size="small" title="Project cost entries">
        {rows.length === 0 ? (
          <Empty description="No posted costs" />
        ) : (
          <Timeline
            items={rows.map((c) => ({
              children: `${c.source_document_number ?? c.id} — ${c.pmv_cost_kind ?? c.cost_category}: ${Number(c.total_cost).toFixed(2)}${c.gl_posted ? '' : ' (operational attribution)'}`,
            }))}
          />
        )}
      </Card>
    </Space>
  );
};

const Pmv360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'overview';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);

  const wsQ = useQuery({
    queryKey: ['pmv-workspace', id],
    queryFn: () => pmvService.getWorkspace(id!),
    enabled: Boolean(id),
  });

  const connectedQ = useQuery({
    queryKey: ['pmv-connected-state', id],
    queryFn: () => pmvService.getEquipmentConnectedState(id!),
    enabled: Boolean(id),
    retry: false,
  });

  const cancelReservationMut = useMutation({
    mutationFn: (reservationId: string) => pmvService.cancelReservation(reservationId),
    onSuccess: () => {
      message.success('Reservation cancelled');
      void qc.invalidateQueries({ queryKey: ['pmv-workspace', id] });
    },
    onError: (e: Error) => message.error(e.message || 'Cancel failed'),
  });

  const ws = wsQ.data as Record<string, any> | undefined;
  const profile = ws?.profile;
  const asset = ws?.asset;
  const classification = ws?.classification;
  const readiness = ws?.readiness as PmvReadinessResult | undefined;
  const currentReservation = ws?.operational?.reservation as
    | { id?: string; from_date?: string; until_date?: string; project_id?: string; status?: string }
    | null
    | undefined;
  const specifications = (ws?.specifications ?? []) as Array<{
    definition: { id: string; code: string; label: string; group_name?: string; data_type: string; unit_of_measure?: string };
    value: { value_text?: string; value_number?: number } | null;
    required?: boolean;
  }>;

  const groupedSpecs = useMemo(() => {
    const g = new Map<string, typeof specifications>();
    for (const s of specifications) {
      const gn = s.definition.group_name || 'General';
      if (!g.has(gn)) g.set(gn, []);
      g.get(gn)!.push(s);
    }
    return g;
  }, [specifications]);

  if (wsQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!profile || !asset) {
    return <Empty description="PMV profile not found" />;
  }

  const allocCurrent = (ws?.allocation as { current?: Record<string, unknown> } | undefined)?.current;
  const projectIdForHero =
    ws?.operational?.allocation?.state === 'ACTIVE'
      ? String(ws.operational.allocation.project_id)
      : allocCurrent?.project_id
        ? String(allocCurrent.project_id)
        : currentReservation?.project_id ?? null;
  const primary = passportPrimaryAction({
    operationalStatus: profile.operational_status,
    allocationStatus: allocCurrent ? String(allocCurrent.allocation_status ?? '') : null,
    profileId: id!,
    allocationId: allocCurrent?.id ? String(allocCurrent.id) : null,
  });
  const makeModel = [asset.manufacturer, asset.model].filter(Boolean).join(' ') || asset.asset_name || 'Equipment';

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Equipment Passport"
          documentNumber={profile.fleet_number || asset.asset_code}
          statusTag={
            <Space size={4} wrap>
              <Tag color={profile.operational_status === 'AVAILABLE' ? 'green' : profile.operational_status === 'BREAKDOWN' ? 'red' : 'blue'}>
                {profile.operational_status.replace(/_/g, ' ')}
              </Tag>
              {connectedQ.data?.connected ? (
                <Tag color={connectivityTagColor(connectedQ.data.connectivity)}>
                  Connectivity: {connectivityLabel(connectedQ.data.connectivity)}
                </Tag>
              ) : (
                <Tag>Not connected</Tag>
              )}
            </Space>
          }
          subtitle={
            <Space direction="vertical" size={2}>
              <Text strong>{makeModel}</Text>
              <Text type="secondary">
                Asset {asset.asset_code}
                {classification?.name ? ` · ${classification.name}` : ''}
                {projectIdForHero
                  ? ` · ${resolveProjectDisplayFromMap(projectLabels, projectIdForHero)}`
                  : ''}
                {connectedQ.data?.connected && connectedQ.data.state
                  ? ` · Last seen ${formatLastSeen((connectedQ.data.state as { last_seen_at?: string }).last_seen_at)}`
                  : ''}
              </Text>
            </Space>
          }
          primaryActions={
            <Space wrap>
              {primary ? (
                <Button type="primary" onClick={() => navigate(primary.href)}>
                  {primary.label}
                </Button>
              ) : null}
              <Button
                icon={<PrinterOutlined />}
                onClick={() =>
                  openPmvPassportPrint({
                    assetCode: asset.asset_code || '',
                    assetName: asset.asset_name || undefined,
                    fleetNumber: profile.fleet_number || undefined,
                    makeModel,
                    classification: classification?.name || undefined,
                    operationalStatus: profile.operational_status,
                    ownershipType: profile.ownership_type,
                    criticality: profile.criticality || undefined,
                    projectLabel: projectIdForHero
                      ? resolveProjectDisplayFromMap(projectLabels, projectIdForHero)
                      : undefined,
                    meterLabel: ws?.operational?.meter?.label || undefined,
                    allocationLabel:
                      ws?.operational?.allocation?.state === 'ACTIVE'
                        ? `${ws.operational.allocation.allocation_status} — ${ws.operational.allocation.planned_start} → ${ws.operational.allocation.planned_end}`
                        : (ws?.operational?.allocation as { label?: string })?.label || 'None',
                    attention: (ws?.attention as string[]) || [],
                    readinessSummary: readiness
                      ? `${(readiness as { overall?: string }).overall || 'See passport'} · blockers ${
                          (readiness as { blockers?: unknown[] }).blockers?.length ?? 0
                        }`
                      : undefined,
                  })
                }
              >
                Print
              </Button>
              <Button onClick={() => navigate('/asset/pmv')}>Fleet</Button>
              <Button
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.set('tab', 'components');
                  setSearchParams(next);
                }}
              >
                Components
              </Button>
              <Button onClick={() => navigate(`/asset/pmv/${id}/tyres`)}>Tyres</Button>
              <Button onClick={() => navigate(`/asset/pmv/${id}/batteries`)}>Batteries</Button>
              <Button onClick={() => navigate(`/asset/pmv/${id}/status`)}>Change status</Button>
              <Button type="link" onClick={() => navigate(`/asset/assets/${asset.id}/edit`)}>
                Asset record
              </Button>
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Card title="Operational" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Status">{profile.operational_status}</Descriptions.Item>
                <Descriptions.Item label="Ownership">{profile.ownership_type}</Descriptions.Item>
                <Descriptions.Item label="Criticality">{profile.criticality || '—'}</Descriptions.Item>
                <Descriptions.Item label="Allocation">
                  {ws?.operational?.allocation?.state === 'ACTIVE'
                    ? `${ws.operational.allocation.allocation_status} — ${ws.operational.allocation.planned_start} → ${ws.operational.allocation.planned_end}`
                    : (ws?.operational?.allocation as { label?: string })?.label ?? 'None'}
                </Descriptions.Item>
                <Descriptions.Item label="Reservation">
                  {currentReservation?.from_date ? (
                    <Space direction="vertical" size={4}>
                      <Text>
                        {currentReservation.from_date} → {currentReservation.until_date}
                        {currentReservation.project_id
                          ? ` — ${resolveProjectDisplayFromMap(projectLabels, currentReservation.project_id)}`
                          : ''}
                      </Text>
                      {currentReservation.id && (
                        <Button
                          size="small"
                          danger
                          loading={cancelReservationMut.isPending}
                          onClick={() => {
                            Modal.confirm({
                              title: 'Cancel reservation?',
                              content: 'Release the active reservation on this unit.',
                              okText: 'Cancel reservation',
                              okButtonProps: { danger: true },
                              onOk: () => cancelReservationMut.mutateAsync(currentReservation.id!),
                            });
                          }}
                        >
                          Cancel reservation
                        </Button>
                      )}
                    </Space>
                  ) : (
                    'None'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Meter">{ws?.operational?.meter?.label}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="Readiness" size="small">
              {readiness ? <ReadinessPanel readiness={readiness} /> : <Spin />}
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="Attention" size="small">
              {(ws?.attention as string[])?.length ? (
                (ws?.attention as string[]).map((a) => <Alert key={a} type="warning" message={a} style={{ marginBottom: 8 }} />)
              ) : (
                <Text type="secondary">No blockers</Text>
              )}
            </Card>
          </Col>
        </Row>

        <Tabs
          activeKey={tab}
          onChange={(k) => {
            const next = new URLSearchParams(searchParams);
            next.set('tab', k);
            setSearchParams(next);
          }}
          items={[
            {
              key: 'overview',
              label: 'Overview',
              children: (
                <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                  <Descriptions.Item label="Asset code">{asset.asset_code}</Descriptions.Item>
                  <Descriptions.Item label="Fleet / unit">{profile.fleet_number || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Classification">{classification?.name}</Descriptions.Item>
                  <Descriptions.Item label="Family">{classification?.class_family}</Descriptions.Item>
                  <Descriptions.Item label="Manufacturer">{asset.manufacturer || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Model">{asset.model || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Serial">{asset.serial_number || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Asset lifecycle">{asset.status}</Descriptions.Item>
                </Descriptions>
              ),
            },
            {
              key: 'components',
              label: 'Components',
              children: id ? (
                <PmvEquipmentComponentsPanel
                  profileId={id}
                  readiness={
                    (ws?.components as {
                      tyres?: { label: string; fitted: number; slots: number; attention: number };
                      batteries?: { label: string; fitted: number; slots: number; attention: number };
                      policy_note?: string;
                    }) || null
                  }
                />
              ) : (
                <Empty />
              ),
            },
            {
              key: 'specs',
              label: 'Specifications',
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button onClick={() => navigate(`/asset/pmv/${id}/specifications/edit`)}>Edit specifications</Button>
                  {[...groupedSpecs.entries()].map(([group, items]) => (
                    <Card key={group} title={group} size="small">
                      <Descriptions column={1} size="small">
                        {items.map((s) => (
                          <Descriptions.Item
                            key={s.definition.id}
                            label={
                              <>
                                {s.definition.label}
                                {s.required ? <Tag color="red">Required</Tag> : null}
                              </>
                            }
                          >
                            {s.value?.value_text ??
                              (s.value?.value_number != null
                                ? `${s.value.value_number}${s.definition.unit_of_measure ? ` ${s.definition.unit_of_measure}` : ''}`
                                : '—')}
                          </Descriptions.Item>
                        ))}
                      </Descriptions>
                    </Card>
                  ))}
                </Space>
              ),
            },
            {
              key: 'docs',
              label: 'Documents',
              children: asset.id ? (
                <EntityAttachmentsCard entityType="Asset" entityId={asset.id} title="Asset documents" />
              ) : null,
            },
            {
              key: 'history',
              label: 'History',
              children: (
                <Timeline
                  items={(ws?.statusHistory as Array<{ to_status: string; reason_text?: string; changed_at: string }>)?.map(
                    (h) => ({
                      children: `${h.to_status}${h.reason_text ? ` — ${h.reason_text}` : ''} (${new Date(h.changed_at).toLocaleString()})`,
                    })
                  )}
                />
              ),
            },
            {
              key: 'allocation',
              label: 'Deployment',
              children: (() => {
                const alloc = ws?.allocation as { current?: Record<string, unknown>; history?: Array<Record<string, unknown>>; dispatch?: Record<string, unknown> } | undefined;
                const cur = alloc?.current;
                if (!cur) return <Empty description="No active allocation." />;
                return (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Card size="small" title="Current deployment">
                      <Descriptions column={1} size="small">
                        <Descriptions.Item label="Allocation">{String(cur.allocation_number ?? cur.id)}</Descriptions.Item>
                        <Descriptions.Item label="Status">{String(cur.allocation_status)}</Descriptions.Item>
                        <Descriptions.Item label="Project">
                          {resolveProjectDisplayFromMap(projectLabels, String(cur.project_id))}
                        </Descriptions.Item>
                        <Descriptions.Item label="Period">{String(cur.planned_start)} → {String(cur.planned_end)}</Descriptions.Item>
                      </Descriptions>
                      <Button type="link" onClick={() => navigate(`/asset/pmv/allocations/${String(cur.id)}`)}>
                        Open allocation workspace
                      </Button>
                    </Card>
                    {alloc?.dispatch && (
                      <Card size="small" title="Dispatch">
                        <Tag>{String((alloc.dispatch as { status?: string }).status)}</Tag>
                      </Card>
                    )}
                    {(alloc?.history?.length ?? 0) > 0 && (
                      <Card size="small" title="History">
                        <Timeline
                          items={alloc!.history!.map((h) => ({
                            children: `${h.allocation_number ?? h.id} — ${h.allocation_status} (${h.planned_start} → ${h.planned_end})`,
                          }))}
                        />
                      </Card>
                    )}
                  </Space>
                );
              })(),
            },
            {
              key: 'inspection',
              label: 'Inspection & Compliance',
              children: id ? (
                <Pmv360InspectionPanel profileId={id} navigate={navigate} />
              ) : (
                <Empty />
              ),
            },
            {
              key: 'maintenance',
              label: 'Maintenance',
              children: id ? (
                <>
                  <WorkshopPassportStrip pmvProfileId={id} />
                  <Pmv360MaintenancePanel
                    profileId={id}
                    navigate={navigate}
                    maintenance={ws?.maintenance}
                  />
                </>
              ) : (
                <Empty />
              ),
            },
            {
              key: 'reliability',
              label: 'Health & Reliability',
              children: id ? (
                <Pmv360ReliabilityPanel profileId={id} navigate={navigate} />
              ) : (
                <Empty />
              ),
            },
            {
              key: 'fuel',
              label: 'Fuel operations',
              children: id ? <Pmv360FuelPanel profileId={id} navigate={navigate} /> : <Empty />,
            },
            {
              key: 'hire',
              label: 'Hire commercial',
              children: id ? (
                <Pmv360HirePanel
                  profileId={id}
                  ownershipType={profile?.ownership_type}
                  navigate={navigate}
                />
              ) : (
                <Empty />
              ),
            },
            {
              key: 'operators',
              label: 'Operators',
              children: id ? (
                <Pmv360OperatorsPanel
                  profileId={id}
                  navigate={navigate}
                  operators={ws?.operators as { assignments?: PmvOperatorAssignment[]; current?: PmvOperatorAssignment | null }}
                />
              ) : (
                <Empty />
              ),
            },
            {
              key: 'connected',
              label: 'Connected',
              children: id ? <PmvPassportConnectedPanel profileId={id} /> : <Empty />,
            },
            {
              key: 'usage',
              label: 'Operations',
              children: id ? <Pmv360UsagePanel profileId={id} navigate={navigate} /> : <Empty />,
            },
            {
              key: 'cost',
              label: 'Cost',
              children: id ? (
                <Pmv360CostPanel
                  profileId={id}
                  projectId={
                    ws?.operational?.allocation?.state === 'ACTIVE'
                      ? String(ws.operational.allocation.project_id)
                      : null
                  }
                  costs={ws?.costs as { projectCosts?: Array<Record<string, unknown>>; summary?: { totalActual?: number; byKind?: Array<{ kind: string; total: number }> } }}
                />
              ) : (
                <Empty />
              ),
            },
          ]}
        />
      </Space>
    </WorkspaceLayout>
  );
};

export default Pmv360Page;
